import type { CreateJobRequest, CreateJobResponse, JobResponse } from "@surevai/contracts";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

export interface ServiceAvailability {
  imageReady: boolean;
  textReady: boolean;
  busy: boolean;
  activeJobs: number;
  maxActiveJobs: number;
}

export async function getServiceAvailability(): Promise<ServiceAvailability> {
  const response = await fetch(`${API_BASE}/health`, { cache: "no-store" });
  if (!response.ok) return { imageReady: false, textReady: false, busy: false, activeJobs: 0, maxActiveJobs: 0 };
  return response.json() as Promise<ServiceAvailability>;
}

async function expectJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const message = body && typeof body.message === "string" ? body.message : "The request could not be completed.";
    throw new Error(message);
  }
  return body as T;
}

export async function createJob(input: CreateJobRequest): Promise<CreateJobResponse> {
  return expectJson<CreateJobResponse>(
    await fetch(`${API_BASE}/api/v1/jobs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
  );
}

export async function uploadImage(url: string, file: File, headers: Record<string, string>): Promise<void> {
  const response = await fetch(url, { method: "PUT", headers, body: file });
  if (!response.ok) throw new Error("The image upload failed. Please try again.");
}

export async function completeJob(jobId: string, accessToken: string): Promise<void> {
  await expectJson(
    await fetch(`${API_BASE}/api/v1/jobs/${jobId}/complete`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  );
}

export async function getJob(jobId: string, accessToken: string): Promise<JobResponse> {
  return expectJson<JobResponse>(
    await fetch(`${API_BASE}/api/v1/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      cache: "no-store",
    }),
  );
}

export async function deleteJob(jobId: string, accessToken: string): Promise<void> {
  await expectJson(
    await fetch(`${API_BASE}/api/v1/jobs/${jobId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  );
}

export async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

